import { DaysMask, inputDateFormat } from 'constant';
import { getSelectedTime, todayDate } from 'services';
import * as Yup from 'yup';

import {
  AppointmentDataInterface,
  AppointmentInterface,
  GetAppointmentsParamsInterface,
  VisitedServices
} from 'types';

export type AppointmentsState = AppointmentDataInterface | null | undefined;

export interface DayInterface {
  id: number;
  name: string;
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface FormDataInterface {
  time_frame: DateOptionValue;
  time_preference: PeriodOptionValue;
  hour: string;
  minute: string;
  period: string;
  selectedDays: number[];
}

export interface FormDataWithDateRange extends FormDataInterface, DateRange {}

export enum PeriodOptionValue {
  AnyTime = 'any_time',
  ThisTime = 'this_time',
  AfterTime = 'after_time',
  BeforeTime = 'before_time'
}

interface PeriodOption {
  value: PeriodOptionValue;
  label: string;
}

export enum DateOptionValue {
  ThisWeek = 'this_week',
  NextWeek = 'next_week',
  ThisMonth = 'this_month'
}

interface DateOption {
  value: DateOptionValue;
  label: string;
}

export const PERIOD_OPTIONS: PeriodOption[] = [
  { value: PeriodOptionValue.AnyTime, label: 'Any Time' },
  { value: PeriodOptionValue.ThisTime, label: 'Only At This Time' },
  { value: PeriodOptionValue.AfterTime, label: 'Any Time After' },
  { value: PeriodOptionValue.BeforeTime, label: 'Any Time Before' }
];

export const DATE_OPTIONS: DateOption[] = [
  { value: DateOptionValue.ThisWeek, label: 'This Week' },
  { value: DateOptionValue.NextWeek, label: 'Next Week' },
  { value: DateOptionValue.ThisMonth, label: 'This Month' }
];

export const TIME_OPTIONS = [
  { value: 'AM', label: 'AM' },
  { value: 'PM', label: 'PM' }
];

export const days: DayInterface[] = [
  { id: DaysMask.Sunday, name: 'Su' },
  { id: DaysMask.Monday, name: 'Mo' },
  { id: DaysMask.Tuesday, name: 'Tu' },
  { id: DaysMask.Wednesday, name: 'We' },
  { id: DaysMask.Thursday, name: 'Th' },
  { id: DaysMask.Friday, name: 'Fr' },
  { id: DaysMask.Saturday, name: 'Sa' }
];

export const validationSchema = Yup.object().shape({
  time_frame: Yup.string().required('Time frame is required'),
  time_preference: Yup.string().required('Time preference is required'),
  hour: Yup.string(),
  minute: Yup.string(),
  period: Yup.string(),
  selectedDays: Yup.array()
});

export const getStartAndEndDate = (
  timeFrame: string | DateOptionValue
): DateRange => {
  const dateFormat = inputDateFormat;

  switch (timeFrame) {
    case DateOptionValue.ThisWeek:
      return {
        startDate: todayDate.startOf('week').format(dateFormat),
        endDate: todayDate.endOf('week').format(dateFormat)
      };
    case DateOptionValue.NextWeek:
      return {
        startDate: todayDate.add(1, 'week').startOf('week').format(dateFormat),
        endDate: todayDate.add(1, 'week').endOf('week').format(dateFormat)
      };
    case DateOptionValue.ThisMonth:
      return {
        startDate: todayDate.startOf('week').format(dateFormat),
        endDate: todayDate.endOf('month').format(dateFormat)
      };
    default:
      return {
        startDate: '',
        endDate: ''
      };
  }
};

export const groupAndFormatData = (
  data: AppointmentDataInterface
): Record<string, AppointmentInterface[]> => {
  const formattedData: Record<string, AppointmentInterface[]> = {};

  Object.keys(data).forEach((key) => {
    const entry = data[key];
    const { start_date } = entry;
    if (!formattedData[start_date]) {
      formattedData[start_date] = [];
    }
    formattedData[start_date].push(entry);
  });

  return formattedData;
};

interface TimeField {
  from_time?: string;
  to_time?: string;
  exact_time?: string;
}

export const generateTimeField = (
  hour: string,
  minute: string,
  period: string,
  time_preference: PeriodOptionValue
): TimeField => {
  const timeFormat = 'HH:mm:ss';
  const parsedHour = parseInt(hour, 10);
  const parsedMinute = parseInt(minute, 10);

  let selectedTime = getSelectedTime(parsedHour, parsedMinute);

  if (period === 'PM' && parsedHour !== 12) {
    selectedTime = selectedTime.add(12, 'hour');
  } else if (period === 'AM' && parsedHour === 12) {
    selectedTime = selectedTime.subtract(12, 'hour');
  }

  if (time_preference === PeriodOptionValue.ThisTime) {
    return { exact_time: selectedTime.format(timeFormat) };
  }
  if (time_preference === PeriodOptionValue.AfterTime) {
    const endOfDay = selectedTime.endOf('day').format(timeFormat);
    return { from_time: selectedTime.format(timeFormat), to_time: endOfDay };
  }
  if (time_preference === PeriodOptionValue.BeforeTime) {
    const startOfDay = selectedTime.startOf('day').format(timeFormat);
    return { from_time: startOfDay, to_time: selectedTime.format(timeFormat) };
  }
  return {};
};

export const generateBasicSearchParams = (
  locid: number,
  services: VisitedServices[],
  startDate: string,
  endDate?: string
): GetAppointmentsParamsInterface => ({
  locid,
  services,
  from_date: startDate,
  to_date: endDate || startDate
});

export const generateAdvancedSearchParams = (
  locid: number,
  services: VisitedServices[],
  params: FormDataWithDateRange
): GetAppointmentsParamsInterface => {
  const timeFields = generateTimeField(
    params.hour,
    params.minute,
    params.period,
    params.time_preference
  );
  const searchParams: GetAppointmentsParamsInterface = {
    locid,
    services,
    from_date: params.startDate,
    to_date: params.endDate,
    ...timeFields
  };

  if (params.selectedDays.length > 0) {
    searchParams.days_mask = params.selectedDays.reduce(
      (acc, item) => acc + item,
      0
    );
  }
  return searchParams;
};
